import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Card from "../../../Component/RetailerComponents/Card";
import { TrendingDown, TrendingUp } from "lucide-react";
import { money } from "../../../utils/helpers";
import DateRangePicker from "../../../Component/Common/DateRangePicker";

/* ---------- PIE COLORS ---------- */
const PIE_COLORS = [
  "#2563EB", // blue
  "#16A34A", // green
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#14B8A6", // teal
  "#EC4899", // pink
];

function EmptyState({ text }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-slate-500">
      {text}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="h-full w-full animate-pulse space-y-3">
      {/* fake grid lines */}
      {/* <div className="h-3 w-1/3 rounded bg-slate-200" /> */}
      {/* <div className="h-3 w-1/4 rounded bg-slate-200" /> */}

      <div className="mt-4 h-full rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="h-full w-full rounded-lg bg-slate-200" />
      </div>
    </div>
  );
}

function PieSkeleton() {
  return (
    <div className="h-full w-full animate-pulse flex items-center justify-center">
      <div className="relative h-40 w-40">
        <div className="absolute inset-0 rounded-full bg-slate-200" />
        <div className="absolute inset-6 rounded-full bg-white" />
      </div>
    </div>
  );
}


export default function TrendSection({
  trend = [],
  productPie = [],
  topProduct,
  lowProduct,
  loading = false,
  showTrend = true,
  showProduct = true,
  isRetailerMobile = false,
  startDate,
  endDate,
  onDateChange
}) {
  const hasTrendData = trend.length > 0;
  const hasPieData = productPie.length > 0;
  

  return (
    <div
      className={`grid gap-6 ${
        isRetailerMobile
          ? "grid-cols-1"
          : "grid-cols-1 lg:grid-cols-2"
      }`}
    >
      {/* ================= Revenue Trend ================= */}
      {showTrend && (
      <Card
       title={`Revenue Trend (${
          trend?.length
            ? new Date(trend[0].date).toLocaleDateString("en-IN", {
                month: "long",
                year: "numeric",
              })
            : new Date().toLocaleDateString("en-IN", {
                month: "long",
                year: "numeric",
              })
        })`}
        right={
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={onDateChange}
            isRetailerMobile={isRetailerMobile}
          />
        }
        className={
          isRetailerMobile
            ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
            : ""
        }
        
      >
        <div className="h-72">
          {loading ? (
            <ChartSkeleton/>
          ) : !hasTrendData ? (
            <EmptyState text="No data found" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} barCategoryGap="30%" margin={{ top: 20, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={isRetailerMobile ? "rgba(255,255,255,0.2)" : "#e5e7eb"}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) =>
                    new Date(value).getDate()
                  }
                />
                <YAxis />
                <Tooltip
                  contentStyle={
                    isRetailerMobile
                      ? {
                          background: "rgba(255,255,255,0.1)",
                          backdropFilter: "blur(12px)",
                          border: "1px solid rgba(255,255,255,0.3)",
                          borderRadius: "12px",
                          color: "#fff",
                        }
                      : {}
                  }
                  formatter={(v) => money(v)}
                />
                <Legend />
                <Bar
                  dataKey="revenue"
                  name="Revenue"
                  fill={isRetailerMobile ? "#2563EB" : "#2563EB"}
                  radius={[6, 6, 0, 0]}
                  barSize={24}   // ✅ fixed width
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
      )}

      {/* ================= Product Revenue Share ================= */}
      {showProduct && (
      <Card
        title="Product Revenue Share"
        className={
          isRetailerMobile
            ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
            : ""
        }
        right={
          <div className="flex items-center gap-2">
            {topProduct && (
              <span
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold
                ${
                  isRetailerMobile
                    ? "bg-white/30 backdrop-blur-3xl border border-white/10 text-blue-800 shadow-[0_8px_25px_rgba(37,99,235,0.25)]"
                    : "border border-blue-100 bg-blue-50 text-blue-700"
                }`}
              >
                <TrendingUp className="h-4 w-4" />
                Top: {topProduct.name}
              </span>
            )}

            {lowProduct && (
              <span
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold
                ${
                  isRetailerMobile
                    ? "bg-white/10 backdrop-blur-xl border border-white/30 text-slate-800 shadow-[0_8px_25px_rgba(99,102,241,0.25)]"
                    : "border border-blue-100 bg-white text-slate-700"
                }`}
              >
                <TrendingDown className="h-4 w-4" />
                Low: {lowProduct.name}
              </span>
            )}
          </div>
        }
      >
        <div className="h-72">
          {loading ? (
            <PieSkeleton/>
          ) : !hasPieData ? (
            <EmptyState text="No data found" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
               <Tooltip
                trigger="click"
                formatter={(v) => money(v)}
                contentStyle={{
                  width: "auto",
                  maxWidth: "200px",
                  whiteSpace: "normal",
                  padding: "8px 12px",
                  borderRadius: "10px",
                  fontSize: "12px",

                  ...(isRetailerMobile && {
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(5px)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    color: "#fff",
                  }),
                }}
              />
                <Legend />
                <Pie
                  data={productPie}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={90}
                >
                  {productPie.map((_, index) => (
                    <Cell
                      key={index}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
      )}
    </div>
  );
}

// import {
//   CartesianGrid,
//   Cell,
//   Legend,
//   Line,
//   LineChart,
//   Pie,
//   PieChart,
//   ResponsiveContainer,
//   Tooltip,
//   XAxis,
//   YAxis,
// } from "recharts";
// import Card from "../../../Component/RetailerComponents/Card";
// import { TrendingDown, TrendingUp } from "lucide-react";
// import { money } from "../../../utils/helpers";

// /* ---------- reusable empty / loading state ---------- */
// function EmptyState({ text }) {
//   return (
//     <div className="flex h-full items-center justify-center text-sm text-slate-500">
//       {text}
//     </div>
//   );
// }

// export default function TrendSection({
//   trend = [],
//   productPie = [],
//   topProduct,
//   lowProduct,
//   loading = false,
// }) {
//   const hasTrendData = Array.isArray(trend) && trend.length > 0;
//   const hasPieData = Array.isArray(productPie) && productPie.length > 0;

//   return (
//     <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
//       {/* ================= Revenue Trend ================= */}
//       <Card title="Revenue Trend">
//         <div className="h-72">
//           {loading ? (
//             <EmptyState text="Loading…" />
//           ) : !hasTrendData ? (
//             <EmptyState text="No data found" />
//           ) : (
//             <ResponsiveContainer width="100%" height="100%">
//               <LineChart data={trend}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="date" />
//                 <YAxis />
//                 <Tooltip formatter={(v) => money(v)} />
//                 <Legend />
//                 <Line
//                   type="monotone"
//                   dataKey="revenue"
//                   name="Revenue"
//                   strokeWidth={3}
//                   dot={false}
//                 />
//               </LineChart>
//             </ResponsiveContainer>
//           )}
//         </div>
//       </Card>

//       {/* ================= Product Revenue Share ================= */}
//       <Card
//         title="Product Revenue Share"
//         right={
//           <div className="flex items-center gap-2">
//             {topProduct && (
//               <span className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">
//                 <TrendingUp className="h-4 w-4" />
//                 Top: {topProduct.name}
//               </span>
//             )}
//             {lowProduct && (
//               <span className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
//                 <TrendingDown className="h-4 w-4" />
//                 Low: {lowProduct.name}
//               </span>
//             )}
//           </div>
//         }
//       >
//         <div className="h-72">
//           {loading ? (
//             <EmptyState text="Loading…" />
//           ) : !hasPieData ? (
//             <EmptyState text="No data found" />
//           ) : (
//             <ResponsiveContainer width="100%" height="100%">
//               <PieChart>
//                 <Tooltip formatter={(v) => money(v)} />
//                 <Legend />
//                 <Pie
//                   data={productPie}
//                   dataKey="value"
//                   innerRadius={45}
//                   outerRadius={90}
//                 >
//                   {productPie.map((_, i) => (
//                     <Cell key={i} />
//                   ))}
//                 </Pie>
//               </PieChart>
//             </ResponsiveContainer>
//           )}
//         </div>
//       </Card>
//     </div>
//   );
// }



// import { CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
// import Card from "../../../Component/RetailerComponents/Card";
// import {  TrendingDown, TrendingUp } from "lucide-react";
// import { money } from "../../../utils/helpers";

// export default function TrendSection({ trend, productPie, topProduct, lowProduct }) {
//   return (
//     <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
//       {/* Revenue Trend */}
      
//       <Card title="Revenue Trend">
//             <div className="h-72">
//             <ResponsiveContainer width="100%" height="100%">
//                 <LineChart data={trend}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="date" />
//                 <YAxis />
//                 <Tooltip formatter={(v) => money(v)} />
//                 <Legend />
//                 <Line type="monotone" dataKey="revenue" name="Revenue" strokeWidth={3} dot={false} />
//                 </LineChart>
//             </ResponsiveContainer>
//             </div>
//         </Card>

//       {/* Product Pie */}
//       <Card
//         title="Product Revenue Share"
//        right={
//             <div className="flex items-center gap-2">
//               {topProduct ? (
//                 <span className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">
//                   <TrendingUp className="h-4 w-4" /> Top: {topProduct.name}
//                 </span>
//               ) : null}
//               {lowProduct ? (
//                 <span className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
//                   <TrendingDown className="h-4 w-4" /> Low: {lowProduct.name}
//                 </span>
//               ) : null}
//             </div>
//           }
//       >
//         <div className="h-72">
//           <ResponsiveContainer width="100%" height="100%">
//             <PieChart>
//               <Tooltip formatter={(v) => money(v)} />
//               <Legend />
//               <Pie data={productPie} dataKey="value" innerRadius={45} outerRadius={90}>
//                 {productPie.map((_, i) => <Cell key={i} />)}
//               </Pie>
//             </PieChart>
//           </ResponsiveContainer>
//         </div>
//       </Card>
//     </div>
//   );
// }
